import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Shield, Target, X } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const INPUT = { width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '10px 12px', fontSize: '14px', outline: 'none', borderRadius: 0, fontFamily: 'inherit' };
const LABEL = { display: 'block', fontSize: '9px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--pp-text-3)', marginBottom: '5px' };

function CloseAllModal({ account, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const [closing, setClosing] = useState(false);

  const execute = async () => {
    setClosing(true);
    try {
      const res = await api.closeAllTrades({ account_id: account.metaapi_account_id || account.account_id });
      toast.success(`Closed ${res.closed_positions} positions`);
      onSuccess();
    } catch (e) { toast.error(e.message); }
    setClosing(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--pp-surface)', border: '1px solid rgba(255,51,51,0.5)', padding: 24, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} color="#FF3333" />
            <span style={{ fontWeight: 700, fontSize: 14, color: '#FF3333', letterSpacing: '0.1em' }}>CLOSE ALL TRADES</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer' }}><X size={16} /></button>
        </div>

        {step === 1 && (
          <>
            <p style={{ color: 'var(--pp-text-2)', fontSize: 13, lineHeight: 1.6, margin: '0 0 20px' }}>This will close <strong style={{ color: '#FF3333' }}>ALL open positions</strong> on <strong>{account.name}</strong>. This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ flex: 1, height: 44, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>CANCEL</button>
              <button data-testid="close-all-step2" onClick={() => setStep(2)} style={{ flex: 1, height: 44, background: '#FF3333', border: 'none', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>YES, CLOSE ALL</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p style={{ color: '#FF3333', fontSize: 13, fontWeight: 700, margin: '0 0 12px' }}>FINAL WARNING</p>
            <p style={{ color: 'var(--pp-text-2)', fontSize: 12, margin: '0 0 16px' }}>Type <strong style={{ color: '#FF3333' }}>CONFIRM</strong> below to execute:</p>
            <input data-testid="close-all-confirm-input" style={{ ...INPUT, borderColor: confirmText === 'CONFIRM' ? '#FF3333' : 'var(--pp-border)' }} value={confirmText} onChange={e => setConfirmText(e.target.value.toUpperCase())} placeholder="Type CONFIRM" />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={onClose} style={{ flex: 1, height: 44, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>CANCEL</button>
              <button data-testid="close-all-execute" disabled={confirmText !== 'CONFIRM' || closing} onClick={execute} style={{ flex: 1, height: 44, background: confirmText === 'CONFIRM' ? '#FF3333' : '#333', border: 'none', color: '#fff', fontWeight: 700, fontSize: 12, cursor: confirmText === 'CONFIRM' ? 'pointer' : 'not-allowed' }}>{closing ? 'CLOSING...' : 'EXECUTE'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RiskSettings({ account, onSaved }) {
  const [settings, setSettings] = useState({ daily_profit_goal_usd: '', weekly_profit_goal_usd: '', max_daily_drawdown_usd: '', drawdown_pause_days: 1 });
  const [saving, setSaving] = useState(false);
  const [pauseInfo, setPauseInfo] = useState(null);

  useEffect(() => {
    const accountId = account.metaapi_account_id || account.account_id;
    if (accountId) {
      api.getRiskSettings(accountId).then(s => {
        if (s && Object.keys(s).length) {
          setSettings({
            // Prefer new $ fields; fall back to legacy % for display
            daily_profit_goal_usd:  s.daily_profit_goal_usd || '',
            weekly_profit_goal_usd: s.weekly_profit_goal_usd || '',
            max_daily_drawdown_usd: s.max_daily_drawdown_usd || '',
            drawdown_pause_days:    s.drawdown_pause_days || 1,
          });
          if (s.copying_paused_until && new Date(s.copying_paused_until) > new Date()) {
            setPauseInfo({ until: s.copying_paused_until, reason: s.last_trigger_reason });
          }
        }
      }).catch(() => {});
    }
  }, [account]);

  const save = async () => {
    setSaving(true);
    try {
      const accountId = account.metaapi_account_id || account.account_id;
      await api.setRiskSettings(accountId, {
        daily_profit_goal_usd:  settings.daily_profit_goal_usd  ? Number(settings.daily_profit_goal_usd)  : null,
        weekly_profit_goal_usd: settings.weekly_profit_goal_usd ? Number(settings.weekly_profit_goal_usd) : null,
        max_daily_drawdown_usd: settings.max_daily_drawdown_usd ? Number(settings.max_daily_drawdown_usd) : null,
        drawdown_pause_days: Number(settings.drawdown_pause_days) || 1,
      });
      toast.success('Risk settings saved!');
      if (onSaved) onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', padding: 14, marginTop: 10 }}>
      <p style={{ margin: '0 0 12px', fontSize: 11, fontWeight: 700, color: '#FFB000', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 6 }}><Shield size={13} /> RISK CONTROLS</p>

      {pauseInfo && (
        <div style={{ background: 'rgba(255,176,0,0.1)', border: '1px solid rgba(255,176,0,0.4)', padding: 12, marginBottom: 12 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#FFB000' }}>COPYING PAUSED</p>
          <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-2)' }}>Resumes: {new Date(pauseInfo.until).toLocaleString()}</p>
          {pauseInfo.reason && <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>{pauseInfo.reason}</p>}
          <button data-testid="resume-copying" onClick={async () => {
            try {
              const accountId = account.metaapi_account_id || account.account_id;
              await api.resumeCopying(accountId);
              setPauseInfo(null);
              toast.success('Copying resumed!');
              if (onSaved) onSaved();
            } catch (e) { toast.error(e.message); }
          }} style={{ marginTop: 8, padding: '6px 14px', background: '#FFB000', border: 'none', color: '#000', fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em' }}>RESUME NOW</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={LABEL}>Daily Profit Goal ($)</label>
          <input data-testid="risk-daily-goal-usd" style={INPUT} type="number" value={settings.daily_profit_goal_usd} onChange={e => setSettings(s => ({ ...s, daily_profit_goal_usd: e.target.value }))} placeholder="500" />
        </div>
        <div>
          <label style={LABEL}>Weekly Profit Goal ($)</label>
          <input data-testid="risk-weekly-goal-usd" style={INPUT} type="number" value={settings.weekly_profit_goal_usd} onChange={e => setSettings(s => ({ ...s, weekly_profit_goal_usd: e.target.value }))} placeholder="2000" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <label style={LABEL}>Max Daily Drawdown ($)</label>
          <input data-testid="risk-max-dd-usd" style={INPUT} type="number" value={settings.max_daily_drawdown_usd} onChange={e => setSettings(s => ({ ...s, max_daily_drawdown_usd: e.target.value }))} placeholder="400" />
        </div>
        <div>
          <label style={LABEL}>Pause Copying For</label>
          <select style={{ ...INPUT, cursor: 'pointer' }} value={settings.drawdown_pause_days} onChange={e => setSettings(s => ({ ...s, drawdown_pause_days: e.target.value }))}>
            <option value={0}>Rest of day</option>
            <option value={1}>1 day</option>
            <option value={2}>2 days</option>
            <option value={3}>3 days</option>
          </select>
        </div>
      </div>
      <button data-testid="save-risk-settings" onClick={save} disabled={saving} style={{ width: '100%', height: 36, background: '#FFB000', border: 'none', color: '#000', fontWeight: 700, fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em' }}>{saving ? 'SAVING...' : 'SAVE RISK SETTINGS'}</button>
      <p style={{ margin: '8px 0 0', fontSize: 9, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>When profit goal is hit, all trades close and copying pauses. When drawdown limit is hit, trades close and copying pauses for the selected duration.</p>
    </div>
  );
}

export default function AccountPortfolioPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closeAllModal, setCloseAllModal] = useState(null);
  const [expandedRisk, setExpandedRisk] = useState(null);
  const [showStrategyModal, setShowStrategyModal] = useState(null);
  const [stratForm, setStratForm] = useState({ name: '', description: '', symbol_focus: '', risk_level: 'medium', monthly_return_pct: '', win_rate: '' });

  const load = async () => {
    try {
      const pnl = await api.getAccountsPnl().catch(() => ({ accounts: [], total_balance: 0, total_pnl: 0 }));
      setAccounts(pnl.accounts || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const userMasterCount = accounts.filter(a => a.is_user_master).length;

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}><div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /></div>;

  const totalBalance = accounts.reduce((s, a) => s + (a.balance || 0), 0);
  const totalPnl = accounts.reduce((s, a) => s + (a.pnl || 0), 0);
  const isPos = totalPnl >= 0;

  return (
    <div className="page-container">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)' }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>ACCOUNT PORTFOLIO</h1>
        <button onClick={() => navigate('/link-broker')} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--pp-primary)', border: 'none', color: '#fff', padding: '8px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ LINK BROKER</button>
      </div>

      <div style={{ padding: 20, borderBottom: '1px solid var(--pp-border)' }}>
        <p className="section-label" style={{ marginBottom: 8 }}>Total Portfolio</p>
        <p className="mono" style={{ fontSize: 36, fontWeight: 800, margin: 0 }}>${totalBalance.toLocaleString()}</p>
        <p className="mono" style={{ fontSize: 16, color: isPos ? 'var(--pp-success)' : 'var(--pp-error)', margin: '4px 0 0' }}>{isPos ? '+' : ''}${totalPnl.toFixed(2)} unrealized P&L</p>
      </div>

      <div style={{ padding: '16px 16px 0' }}>
        <p className="section-label" style={{ marginBottom: 12 }}>Connected Accounts</p>
        {accounts.length === 0 ? (
          <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, textAlign: 'center' }}>
            <p style={{ color: 'var(--pp-text-3)', margin: '0 0 16px' }}>No broker accounts connected</p>
            <button onClick={() => navigate('/link-broker')} className="btn-primary" style={{ padding: '10px 24px' }}>Link Broker</button>
          </div>
        ) : (
          accounts.map((acct, i) => {
            const acctKey = acct.metaapi_account_id || acct.account_id || i;
            return (
              <div key={acctKey} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>{acct.name}</p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', marginTop: 2, textTransform: 'capitalize' }}>{acct.platform}</p>
                  </div>
                  {(acct.platform === 'tradovate' || acct.platform === 'volumetrica') && (
                    <button
                      data-testid={`refresh-balance-${acct.account_id}`}
                      onClick={async () => {
                        try {
                          await api.refreshAccountBalance(acct.platform, acct.account_id);
                          toast.info('Refreshing balance — reload in 30-60s');
                          setTimeout(load, 35000);
                          setTimeout(load, 70000);
                        } catch (e) { toast.error(e.message || 'Refresh failed'); }
                      }}
                      style={{ background: 'rgba(41,121,255,0.10)', border: '1px solid rgba(41,121,255,0.35)', color: 'var(--pp-accent)', fontSize: 10, padding: '6px 10px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}
                    >↻ REFRESH</button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
                  {[
                    { l: 'Balance', v: `$${(acct.balance || 0).toLocaleString()}` },
                    { l: 'Equity', v: `$${(acct.equity || 0).toLocaleString()}` },
                    { l: 'P&L', v: `${(acct.pnl || 0) >= 0 ? '+' : ''}$${(acct.pnl || 0).toFixed(2)}`, c: (acct.pnl || 0) >= 0 ? 'var(--pp-success)' : 'var(--pp-error)' },
                  ].map(m => (
                    <div key={m.l} style={{ textAlign: 'center' }}>
                      <p className="section-label" style={{ marginBottom: 4, fontSize: 8 }}>{m.l}</p>
                      <p className="mono" style={{ margin: 0, fontSize: 14, fontWeight: 700, color: m.c || 'var(--pp-text)' }}>{m.v}</p>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button data-testid={`close-all-${i}`} onClick={() => setCloseAllModal(acct)} style={{ flex: 1, minWidth: 100, height: 36, background: 'rgba(255,51,51,0.1)', border: '1px solid rgba(255,51,51,0.4)', color: '#FF3333', fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    <AlertTriangle size={12} /> CLOSE ALL
                  </button>
                  <button data-testid={`risk-toggle-${i}`} onClick={() => setExpandedRisk(expandedRisk === acctKey ? null : acctKey)} style={{ flex: 1, minWidth: 100, height: 36, background: 'rgba(255,176,0,0.1)', border: '1px solid rgba(255,176,0,0.4)', color: '#FFB000', fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    <Target size={12} /> {expandedRisk === acctKey ? 'HIDE' : 'RISK CONTROLS'}
                  </button>
                  {!acct.is_user_master && userMasterCount < 3 && (
                    <button data-testid={`set-master-${i}`} onClick={async () => {
                      try { await api.setMaster({ metaapi_account_id: acct.metaapi_account_id || acct.account_id }); toast.success('Account set as master!'); load(); } catch (e) { toast.error(e.message); }
                    }} style={{ flex: 1, minWidth: 100, height: 36, background: 'rgba(0,200,255,0.1)', border: '1px solid rgba(0,200,255,0.4)', color: '#00C8FF', fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em' }}>
                      SET AS MASTER
                    </button>
                  )}
                  {acct.is_user_master && (
                    <div style={{ display: 'flex', gap: 6, flex: 1, minWidth: 200 }}>
                      <span style={{ color: '#00C8FF', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 4 }}>MASTER</span>
                      <button onClick={() => { setShowStrategyModal(acct.metaapi_account_id || acct.account_id); setStratForm({ name: '', description: '', symbol_focus: '', risk_level: 'medium', monthly_return_pct: '', win_rate: '' }); }} style={{ flex: 1, height: 36, background: 'rgba(0,200,255,0.1)', border: '1px solid rgba(0,200,255,0.3)', color: '#00C8FF', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>+ CREATE STRATEGY</button>
                      <button onClick={async () => { try { await api.unsetMaster({ metaapi_account_id: acct.metaapi_account_id || acct.account_id }); toast.success('Master removed'); load(); } catch (e) { toast.error(e.message); } }} style={{ height: 36, background: 'none', border: '1px solid var(--pp-border)', color: 'var(--pp-text-3)', fontSize: 10, padding: '0 10px', cursor: 'pointer' }}>REMOVE</button>
                    </div>
                  )}
                </div>

                {expandedRisk === acctKey && <RiskSettings account={acct} onSaved={load} />}
              </div>
            );
          })
        )}
      </div>

      {closeAllModal && <CloseAllModal account={closeAllModal} onClose={() => setCloseAllModal(null)} onSuccess={() => { setCloseAllModal(null); load(); }} />}

      {showStrategyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowStrategyModal(null)}>
          <div style={{ background: 'var(--pp-surface)', border: '1px solid rgba(0,200,255,0.3)', padding: 24, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: '#00C8FF', letterSpacing: '0.1em' }}>CREATE STRATEGY</span>
              <button onClick={() => setShowStrategyModal(null)} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer' }}><X size={16} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div><label style={LABEL}>Strategy Name</label><input style={INPUT} value={stratForm.name} onChange={e => setStratForm(f => ({ ...f, name: e.target.value }))} placeholder="My Strategy" /></div>
              <div><label style={LABEL}>Description</label><input style={INPUT} value={stratForm.description} onChange={e => setStratForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe your approach" /></div>
              <div><label style={LABEL}>Symbol Focus</label><input style={INPUT} value={stratForm.symbol_focus} onChange={e => setStratForm(f => ({ ...f, symbol_focus: e.target.value }))} placeholder="EURUSD, Gold, etc." /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div><label style={LABEL}>Risk Level</label>
                  <select style={{ ...INPUT, cursor: 'pointer' }} value={stratForm.risk_level} onChange={e => setStratForm(f => ({ ...f, risk_level: e.target.value }))}>
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </div>
                <div><label style={LABEL}>Monthly %</label><input style={INPUT} type="number" value={stratForm.monthly_return_pct} onChange={e => setStratForm(f => ({ ...f, monthly_return_pct: e.target.value }))} placeholder="5" /></div>
                <div><label style={LABEL}>Win Rate %</label><input style={INPUT} type="number" value={stratForm.win_rate} onChange={e => setStratForm(f => ({ ...f, win_rate: e.target.value }))} placeholder="70" /></div>
              </div>
              <button data-testid="create-strategy-submit" disabled={!stratForm.name} onClick={async () => {
                try {
                  const res = await api.createUserStrategy({ ...stratForm, master_account_id: showStrategyModal });
                  toast.success(`Strategy "${stratForm.name}" created!`);
                  setShowStrategyModal(null);
                } catch (e) { toast.error(e.message); }
              }} style={{ width: '100%', height: 44, background: '#00C8FF', border: 'none', color: '#000', fontWeight: 800, fontSize: 12, cursor: 'pointer', letterSpacing: '0.1em' }}>CREATE STRATEGY</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ height: 20 }} />
    </div>
  );
}
